/**
 * Image utility — client-side compression for community photo uploads.
 * Compresses any JPG/PNG/HEIC (HEIC handled by browser native decoding via
 * Image element on iOS/Safari ≥17) down to a max-dimension JPEG, target ≤500KB.
 */
export async function compressImage(file, { maxDim = 1600, quality = 0.82, targetKB = 500 } = {}) {
  if (!file) return null;
  const reader = new FileReader();
  const dataUrl = await new Promise((resolve, reject) => {
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = dataUrl;
  });

  let { width, height } = img;
  const ratio = Math.min(1, maxDim / Math.max(width, height));
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  // Recompress until ≤ targetKB or quality bottoms out
  let q = quality;
  let result = canvas.toDataURL("image/jpeg", q);
  while (result.length / 1.37 > targetKB * 1024 && q > 0.4) {
    q -= 0.1;
    result = canvas.toDataURL("image/jpeg", q);
  }
  return result;
}
